import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { scrollBarDarkCss } from "../common/layout";
import { usePopper } from "react-popper";
import { Placement } from "@popperjs/core/lib";
import { useClickOutside } from "melodies-source/utils/hooks";
import { ReactComponent as SvgDropdownCaret } from "./dropdown-caret.svg";

export const DropdownBackdrop = styled.div`
  position: fixed;
  top: 0px;
  left: 0px;
  margin: 0px;
  width: 100vw;
  height: 100vh;
  z-index: 99999999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const DropdownContainer = styled.div`
  background-color: var(--component-background-color);
  position: absolute;
  border-radius: 4px;
  overflow-y: auto;
  ${scrollBarDarkCss};
  z-index: 1000000000000;
  box-shadow: 0 8px 20px 0 rgba(51, 72, 115, 0.13);
  border-radius: 0 0 6px 6px;
  &[data-popper-placement*="top"] {
    border-radius: 6px 6px 0 0;
  }
`;

type OffsetModifier = {
  top?: [number, number];
  bottom?: [number, number];
};

const getModifiers = (offset?: OffsetModifier, overflowPadding?: number) => [
  {
    name: "offset",
    options: {
      offset: ({ placement }: { placement: string }) => {
        if (placement.includes("top")) {
          return offset?.top ?? [0, 48];
        } else {
          return offset?.bottom ?? [0, 0];
        }
      },
    },
  },
  {
    name: "preventOverflow",
    options: {
      padding: overflowPadding ?? 8,
    },
  },
];

type Props = React.BaseHTMLAttributes<HTMLDivElement> & {
  onClose?: any;
  isOpen?: boolean;
  placement?: Placement;
  children: any;
  withBackdropClose?: boolean;
  offset?: OffsetModifier;
  overflowPadding?: number;
  strategy?: "absolute" | "fixed";
  showArrow?: boolean;
  // Optional prop to prevent click outside behavior on a specific ref
  // Useful for cases where you want to prevent closing the dropdown when clicking a button element
  preventRef?: HTMLElement | null;
};

export const Dropdown = ({
  onClose,
  isOpen = false,
  placement = "top",
  children,
  withBackdropClose = true,
  offset,
  overflowPadding,
  strategy = "absolute",
  showArrow = false,
  preventRef,
  ...props
}: Props) => {
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );

  useClickOutside(
    popperElement,
    () => {
      if (onClose && !withBackdropClose) onClose();
    },
    preventRef,
  );

  const modifiers = useMemo(() => {
    return getModifiers(offset, overflowPadding);
  }, [offset, overflowPadding]);

  const { styles, attributes, update } = usePopper(
    referenceElement,
    popperElement,
    {
      placement,
      modifiers,
      strategy,
    },
  );

  useEffect(() => {
    if (update) update();
  }, [isOpen]);

  return (
    isOpen && (
      <div ref={setReferenceElement}>
        {withBackdropClose && (
          <DropdownBackdrop onClick={onClose}></DropdownBackdrop>
        )}
        <DropdownContainer
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
          {...props}
        >
          {showArrow && (
            <CaretContainer
              {...attributes.arrow}
              style={styles.arrow}
              data-popper-arrow
            >
              <Caret />
            </CaretContainer>
          )}
          {children}
        </DropdownContainer>
      </div>
    )
  );
};

const Caret = styled(SvgDropdownCaret)`
  width: 62px;
  height: 54px;
  bottom: -15px;
`;

const CaretContainer = styled.div`
  display: flex;
  position: absolute;
  bottom: calc(100% - 1px);
  width: 62px;
  height: 39px;
  overflow: hidden;
`;
